// Runtime settings read from app-config.json next to index.html. CI writes the file per env, so one
// build can be deployed to several web hosts and packaged into several apps.
import { isEnv } from "./net/env";

export interface AppConfig {
  /** Backend WebSocket endpoint, without ?env=. */
  wsUrl: string;
  /** Env to connect with until the server pins the player to one (e.g. "beta" on the beta web host). */
  env?: string;
}

export const APP_CONFIG_URL = "app-config.json";
const FETCH_TIMEOUT_MS = 5000;

export type FetchLike = (url: string, init: { cache: "no-cache"; signal: AbortSignal }) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}>;

/** Validates a parsed app-config.json. An unusable file gives `defaults`; an invalid env is dropped. */
export function parseAppConfig(value: unknown, defaults: AppConfig): { config: AppConfig; warnings: string[] } {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { config: defaults, warnings: ["not a JSON object"] };
  }
  const { wsUrl, env } = value as Record<string, unknown>;
  if (!isWebSocketUrl(wsUrl)) return { config: defaults, warnings: ["wsUrl must be a ws:// or wss:// URL"] };
  const config: AppConfig = { wsUrl };
  const warnings: string[] = [];
  if (isEnv(env)) config.env = env;
  else if (env !== undefined) warnings.push(`ignoring invalid env ${JSON.stringify(env)}`);
  return { config, warnings };
}

/** Fetches app-config.json relative to the page, bypassing the HTTP cache. Never rejects. */
export async function loadAppConfig(
  defaults: AppConfig,
  fetchFn: FetchLike = (url, init) => fetch(url, init),
  url = APP_CONFIG_URL,
): Promise<AppConfig> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  let value: unknown;
  try {
    const response = await fetchFn(url, { cache: "no-cache", signal: controller.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    value = await response.json();
  } catch (err) {
    console.warn(`${url}: using defaults:`, err);
    return defaults;
  } finally {
    clearTimeout(timer);
  }
  const { config, warnings } = parseAppConfig(value, defaults);
  for (const warning of warnings) console.warn(`${url}: ${warning}${config === defaults ? "; using defaults" : ""}`);
  return config;
}

function isWebSocketUrl(value: unknown): value is string {
  if (typeof value !== "string") return false;
  try {
    const { protocol } = new URL(value);
    return protocol === "ws:" || protocol === "wss:";
  } catch {
    return false;
  }
}
